import { getBaseUrl } from './baseUrl';

export type PlatformSupportUser = {
  id: string;
  username: string;
  email: string | null;
  fullName: string | null;
  role: 'SUPPORT_ADMIN' | 'SUPPORT_AGENT';
  isActive: boolean;
  lastLoginAt: string | null;
};

export type PlatformSupportSession = {
  token: string;
  tokenType: string;
  user: PlatformSupportUser;
};

export type PlatformSupportLicense = {
  id: string;
  licenseNumber: string;
  scopeType: 'UNIT' | 'GROUP';
  status: string;
  health: string;
  customerName: string | null;
  target: { id: string; name: string; slug: string; isActive?: boolean } | null;
  termMonths: number;
  startsAt: string;
  expiresAt: string;
  activatedAt: string | null;
  expiryWarning: { level: string; daysRemaining: number; expiresAt: string } | null;
  totals: { seats: number; assigned: number; suspended: number };
  scheduledTotals: { seats: number; assigned: number; suspended: number };
  historicalTotals: { seats: number; assigned: number; suspended: number };
  seatPools: Array<{
    product: { id: string; code: string; label: string; description: string | null; scopeType: string };
    quantity: number;
    assignedCount: number;
    activeAssignedCount: number;
    suspendedCount: number;
    availableCount: number;
    overAllocationDetectedAt: string | null;
    overAllocationGraceEndsAt: string | null;
    purchases: PlatformSupportLicense['entitlements'];
  }>;
  entitlements: Array<{
    id: string;
    quantity: number;
    startsAt: string;
    expiresAt: string;
    status: 'ACTIVE' | 'CANCELLED';
    termType: 'PRIMARY_ADMIN' | 'CO_TERM' | 'CUSTOM';
    label: string | null;
    notes: string | null;
    cancelledAt: string | null;
    cost: { amountMinor: number; currency: string | null; label: string };
    renewalMode: 'MANUAL' | 'SUPPORT_MANAGED' | 'BILLING_MANAGED';
    renewalStatus: 'NOT_APPLICABLE' | 'ACTIVE' | 'PAUSED' | 'PAYMENT_FAILED';
    renewalQuantity: number;
    renewalIntervalMonths: number | null;
    renewalFailureAt: string | null;
    renewalFailureReason: string | null;
    expiryAttention: { thresholdDays: 30 | 60; daysRemaining: number; quantity: number } | null;
    lifecycleState: 'CURRENT' | 'SCHEDULED' | 'EXPIRED' | 'CANCELLED';
    product: { id: string; code: string; label: string; description: string | null; scopeType: string };
    assignedCount: number;
    activeAssignedCount: number;
    suspendedCount: number;
    availableCount: number;
    overAllocationDetectedAt: string | null;
    overAllocationGraceEndsAt: string | null;
  }>;
  activationKeys: Array<{
    id: string;
    keyPrefix: string;
    status: string;
    expiresAt: string;
    consumedAt: string | null;
    createdAt: string;
    activatedOrganization: { id: string; name: string; slug: string } | null;
  }>;
};

export type PlatformSupportLicenseList = {
  records: PlatformSupportLicense[];
  graceDays: number;
  pagination: { page: number; pageSize: number; total: number; totalPages: number };
  summary: { totalLicenses: number; activeLicenses: number; purchasedSeats: number; assignedSeats: number; attentionOnPage: number };
  filters: { search: string; status: string | null; scopeType: string | null };
};

export type IssuedLicenseResult = {
  activationKey: string | null;
  license: {
    id: string;
    licenseNumber: string;
    customerName: string | null;
    scopeType: 'UNIT' | 'GROUP';
    status: string;
    termMonths: number;
    startsAt: string;
    expiresAt: string;
    entitlements: Array<{
      id: string;
      quantity: number;
      product: { id: string; code: string; label: string; scopeType: string };
    }>;
    activation: { id: string; keyPrefix: string; status: string; expiresAt: string } | null;
  };
};

export type PlatformSupportLicenseTarget = {
  id: string;
  name: string;
  slug: string;
  currentLicense: { id: string; licenseNumber: string; status: string; expiresAt: string } | null;
  requiredSeats: Record<string, number>;
};

export type PlatformSupportLicenseTargets = {
  units: PlatformSupportLicenseTarget[];
  orgs: PlatformSupportLicenseTarget[];
};

const STORAGE_KEY = 'bonzaiq.platformSupport.session';

async function parseResponse<T>(response: Response): Promise<T> {
  const payload = await response.json().catch(() => null) as T | { error?: string } | null;
  if (!response.ok) {
    const message = payload && typeof payload === 'object' && 'error' in payload && payload.error
      ? String(payload.error)
      : 'Platform support request failed';
    throw new Error(message);
  }
  return payload as T;
}

export function loadPlatformSupportSession(): PlatformSupportSession | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PlatformSupportSession;
    return parsed?.token && parsed?.user?.id ? parsed : null;
  } catch {
    return null;
  }
}

export function savePlatformSupportSession(session: PlatformSupportSession): void {
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearPlatformSupportSession(): void {
  window.localStorage.removeItem(STORAGE_KEY);
}

export async function loginPlatformSupport(username: string, password: string): Promise<PlatformSupportSession> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ username, password })
  });
  return parseResponse<PlatformSupportSession>(response);
}

export async function fetchPlatformSupportSession(token: string): Promise<PlatformSupportUser> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  const payload = await parseResponse<{ user: PlatformSupportUser }>(response);
  return payload.user;
}

export async function logoutPlatformSupport(token: string): Promise<void> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/auth/logout`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!response.ok) {
    await parseResponse(response);
  }
}

export async function fetchPlatformSupportLicenses(
  token: string,
  filters: { page?: number; pageSize?: number; search?: string; status?: string; scopeType?: string } = {}
): Promise<PlatformSupportLicenseList> {
  const base = await getBaseUrl();
  const query = new URLSearchParams();
  if (filters.page) query.set('page', String(filters.page));
  if (filters.pageSize) query.set('pageSize', String(filters.pageSize));
  if (filters.search) query.set('search', filters.search);
  if (filters.status && filters.status !== 'ALL') query.set('status', filters.status);
  if (filters.scopeType && filters.scopeType !== 'ALL') query.set('scopeType', filters.scopeType);
  const response = await fetch(`${base}/api/platform-support/licensing/licenses?${query.toString()}`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  return parseResponse<PlatformSupportLicenseList>(response);
}

export async function issuePlatformSupportLicense(
  token: string,
  input: {
    targetMode: 'NEW_UNIT' | 'EXISTING_UNIT' | 'EXISTING_GROUP';
    customerName?: string;
    targetId?: string;
    termMonths: number;
    activationDays: number;
    notes?: string;
    seatQuantities: Record<string, number>;
  }
): Promise<IssuedLicenseResult> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/licenses`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseResponse<IssuedLicenseResult>(response);
}

export async function fetchPlatformSupportLicenseTargets(token: string): Promise<PlatformSupportLicenseTargets> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/targets`, {
    headers: { Authorization: `Bearer ${token}` },
    cache: 'no-store'
  });
  return parseResponse<PlatformSupportLicenseTargets>(response);
}

export async function createPlatformSupportSeatPurchase(
  token: string,
  licenseId: string,
  input: {
    productCode: string;
    quantity: number;
    termType: 'CO_TERM' | 'CUSTOM';
    startsAt?: string;
    expiresAt?: string;
    notes?: string;
  }
): Promise<{ seatPurchaseId: string }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/licenses/${licenseId}/seat-purchases`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseResponse<{ seatPurchaseId: string }>(response);
}

export async function changePlatformSupportSeatPurchase(
  token: string,
  licenseId: string,
  entitlementId: string,
  input: { quantity?: number; expiresAt?: string; renewalQuantity?: number; renewalIntervalMonths?: number; notes?: string }
): Promise<{ seatPurchaseId: string; allocation: { state: string; overBy: number; graceEndsAt: string | null }; siteAccessUntil: string | null }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/licenses/${licenseId}/seat-purchases/${entitlementId}`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseResponse(response);
}

export type SeatCapacityPlanLine = {
  productCode: string;
  quantity: number;
  startsAt: string;
  expiresAt: string;
  renewalQuantity?: number;
  renewalIntervalMonths?: number;
  notes?: string;
};

export type PlatformSupportTicketSummary = {
  id: string; ticketNumber: string; requesterName: string; requesterEmail: string | null; requesterRole: string | null;
  applicationSurface: string; pageLabel: string | null; category: string; priority: string; status: string; subject: string;
  createdAt: string; updatedAt: string; organization: { id: string; name: string; slug: string };
  assignedSupportUser: { id: string; username: string; fullName: string | null; role: string } | null;
  _count: { messages: number };
};

export type PlatformSupportTicketAttachment = { id: string; messageId: string | null; fileName: string; mimeType: string | null; sizeBytes: number; createdAt: string; downloadPath: string; previewPath: string };

export type PlatformSupportTicketDetail = PlatformSupportTicketSummary & {
  pagePath: string | null; description: string; diagnosticSnapshot: Record<string, unknown>;
  attachments: PlatformSupportTicketAttachment[];
  messages: Array<{ id: string; authorType: string; authorName: string | null; body: string; isInternal: boolean; createdAt: string; attachments: PlatformSupportTicketAttachment[] }>;
  events: Array<{
    id: string; action: string; previousStatus: string | null; nextStatus: string | null; details: unknown; createdAt: string;
    requesterUser: { id: string; username: string; fullName: string | null } | null;
    supportUser: { id: string; username: string; fullName: string | null } | null;
  }>;
};

export async function createPlatformSupportSeatCapacityPlan(
  token: string,
  licenseId: string,
  input: { purchases: SeatCapacityPlanLine[]; notes?: string }
): Promise<{ seatPurchaseIds: string[]; createdCount: number; siteAccessUntil: string | null }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/licenses/${licenseId}/seat-purchases/bulk`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({
      ...input,
      purchases: input.purchases.map(line => ({ ...line, termType: 'CUSTOM' }))
    })
  });
  return parseResponse(response);
}

export async function cancelPlatformSupportSeatPurchase(
  token: string,
  licenseId: string,
  entitlementId: string,
  input: { notes?: string }
): Promise<{ seatPurchaseId: string; status: 'CANCELLED'; affectedAssignments: number; allocation: { state: string; overBy: number; graceEndsAt: string | null }; graceDays: number; siteAccessUntil: string | null }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/licenses/${licenseId}/seat-purchases/${entitlementId}/cancel`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseResponse(response);
}

export async function changePlatformSupportLicenseLifecycle(
  token: string,
  licenseId: string,
  input: { action: 'SUSPEND' | 'REACTIVATE' | 'REVOKE'; notes?: string }
): Promise<{ licenseId: string; status: string; affectedAssignments: number }> {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/licensing/licenses/${licenseId}/lifecycle`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(input)
  });
  return parseResponse(response);
}

export async function fetchPlatformSupportTickets(token: string, filters: { page?: number; pageSize?: number; search?: string; status?: string } = {}) {
  const base = await getBaseUrl();
  const query = new URLSearchParams();
  if (filters.page) query.set('page', String(filters.page));
  if (filters.pageSize) query.set('pageSize', String(filters.pageSize));
  if (filters.search) query.set('search', filters.search);
  if (filters.status && filters.status !== 'ALL') query.set('status', filters.status);
  const response = await fetch(`${base}/api/platform-support/tickets?${query}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  return parseResponse<{ tickets: PlatformSupportTicketSummary[]; pagination: { page: number; pageSize: number; total: number; totalPages: number } }>(response);
}

export async function fetchPlatformSupportTicket(token: string, ticketId: string) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/tickets/${ticketId}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  return parseResponse<{ ticket: PlatformSupportTicketDetail }>(response);
}

export async function fetchPlatformSupportTicketUsers(token: string) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/tickets/support-users`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  return parseResponse<{ users: Array<{ id: string; username: string; fullName: string | null; role: string }> }>(response);
}

export async function updatePlatformSupportTicket(token: string, ticketId: string, input: { priority?: string; assignedSupportUserId?: string | null }) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/tickets/${ticketId}`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  return parseResponse<{ ticket: PlatformSupportTicketDetail }>(response);
}

export async function performPlatformSupportTicketAction(token: string, ticketId: string, action: 'ESCALATE' | 'RESOLVE' | 'DISMISS') {
  const base = await getBaseUrl();
  const response = await fetch(`${base}/api/platform-support/tickets/${ticketId}/actions`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ action })
  });
  return parseResponse<{ ticket: PlatformSupportTicketDetail }>(response);
}

export async function addPlatformSupportTicketMessage(token: string, ticketId: string, input: { body: string; isInternal: boolean; files?: File[] }) {
  const base = await getBaseUrl();
  const form = new FormData();
  form.append('body', input.body); form.append('isInternal', String(input.isInternal));
  (input.files || []).forEach(file => form.append('files', file));
  const response = await fetch(`${base}/api/platform-support/tickets/${ticketId}/messages`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: form });
  return parseResponse<{ message: PlatformSupportTicketDetail['messages'][number] }>(response);
}

export async function downloadPlatformSupportTicketAttachment(token: string, attachment: PlatformSupportTicketDetail['attachments'][number]) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}${attachment.downloadPath}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  if (!response.ok) await parseResponse(response);
  const url = URL.createObjectURL(await response.blob());
  const anchor = document.createElement('a'); anchor.href = url; anchor.download = attachment.fileName; anchor.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function fetchPlatformSupportTicketAttachmentFile(token: string, attachment: PlatformSupportTicketAttachment) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}${attachment.downloadPath}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  if (!response.ok) await parseResponse(response);
  const blob = await response.blob();
  return { blob, contentType: response.headers.get('Content-Type') || blob.type || attachment.mimeType || 'application/octet-stream' };
}

export async function fetchPlatformSupportTicketAttachmentPreview(token: string, attachment: PlatformSupportTicketAttachment) {
  const base = await getBaseUrl();
  const response = await fetch(`${base}${attachment.previewPath}`, { headers: { Authorization: `Bearer ${token}` }, cache: 'no-store' });
  return parseResponse<{ kind: 'TEXT' | 'DOCUMENT_TEXT' | 'BINARY'; content: string | null }>(response);
}
